/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  addToast,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
  Tab,
  Tabs,
} from '@heroui/react';
import { useTranslation } from 'react-i18next';
import {
  FaArrowLeft,
  FaPhone,
  FaRegEnvelope,
  FaUserTie,
  FaTag,
  FaScaleBalanced,
  FaShieldHalved,
} from 'react-icons/fa6';
import { MdCheckCircle, MdOutlineBlock, MdOutlineBolt } from 'react-icons/md';
import { BiTimeFive, BiCalendarCheck } from 'react-icons/bi';
import {
  FiClock,
  FiMessageCircle,
  FiCalendar,
  FiUser,
  FiUserCheck,
} from 'react-icons/fi';
import {
  useAcceptBroadcastQuote,
  useAcceptJob,
  useCancelJob,
  useCancelRequestChanges,
  useConfirmJobCompletion,
  useDeclineJob,
  useCreateConversation,
  useRequestQuoteChanges,
} from '../../../hook/useMutationData';
import { useFetchJobById, useFetchMyJobs } from '../../../hook/useQueryData';
import CustomButton from '../../components/CustomButton';
import CustomChip from '../../components/CustomChip';
import CustomAlert from '../../components/CustomAlert';
import CommonConfirmModal from '../../components/common/ui/CommonConfirmModal';
import ReviewForm from '../../components/ReviewForm/ReviewForm';
import PaymentScheduleBreakdown from './PaymentScheduleBreakdown';
import {
  getEngagementStatusChipColor,
  getEngagementStatusLabel,
  getJobDisplayStatus,
  getQuoteExpiryInfo,
  ENGAGEMENT_STATUS,
} from '../../../utils/engagement';
import {
  QuoteRequest,
  getSiblingEffectiveState,
  groupJobsIntoRequests,
  pickRepresentativeJob,
} from '../../../utils/quoteFlowRequests';
import { formatCurrency } from '../../../utils/currency';
import { Job, JobStatus } from '../../../utils/type';
import { all_routes } from '../../../core/data/routes/all_routes';
import { FEATURE_FLAGS, useFeatureFlag } from '../../../config/featureFlags';
import ModernCard from '../../components/common/ui/ModernCard';
import { formatDate } from '../../../utils/jobDetail.helpers';
import RequestSummaryCard from '../../components/common/ui/RequestSummaryCard';
import QuoteNegotiationThread from '../../components/common/ui/QuoteNegotiationThread';
import QuoteComparisonPanel, {
  providerLabel,
} from '../../components/common/ui/QuoteComparisonPanel';
import QuoteTrackingPanel from '../../components/common/ui/QuoteTrackingPanel';
import LineItemsBreakdown from '../../components/common/ui/LineItemsBreakdown';
import JobProgressTimeline from '../../components/common/ui/JobProgressTimeline';
import BroadcastProviderSelectModal from '../../components/common/ui/BroadcastProviderSelectModal';
import BroadcastAcceptDeclineModal from '../../components/common/ui/BroadcastAcceptDeclineModal';

const CUSTOMER_CANCEL_REASONS = [
  { value: 'change_in_plans', label: 'Change in plans' },
  { value: 'service_no_longer_required', label: 'Service no longer required' },
  {
    value: 'unable_attend_scheduled_time',
    label: 'Unable to attend at the scheduled time',
  },
  {
    value: 'booking_details_no_longer_meet_requirements',
    label: 'Booking details no longer meet requirements',
  },
  {
    value: 'selected_another_provider',
    label: 'Selected another service provider',
  },
  { value: 'other', label: 'Other reason' },
];

const HORIZONTAL_SUMMARY_THRESHOLD = 3;

/**
 * Main JobDetail Component
 */
const JobDetail = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const acceptMutation = useAcceptJob();
  const declineMutation = useDeclineJob();
  const cancelMutation = useCancelJob();
  const cancelRequestChangesMutation = useCancelRequestChanges();
  const confirmCompletionMutation = useConfirmJobCompletion();
  const createConversation = useCreateConversation();
  const acceptBroadcastMutation = useAcceptBroadcastQuote();
  const requestChangesMutation = useRequestQuoteChanges();
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [acceptTarget, setAcceptTarget] = useState<Job | null>(null);
  const [manualCompareView, setManualCompareView] = useState(false);
  const [showContactProviderModal, setShowContactProviderModal] =
    useState(false);
  const [selectedCancelTargets, setSelectedCancelTargets] = useState<string[]>(
    []
  );
  const [selectedCancelReason, setSelectedCancelReason] = useState<string>('');
  const [hasRequestedChanges, setHasRequestedChanges] = useState(false);
  const [revealSignal, setRevealSignal] = useState(0);
  const [showBroadcastCancelModal, setShowBroadcastCancelModal] =
    useState(false);
  const [broadcastCancelStep, setBroadcastCancelStep] = useState<
    'provider' | 'reason'
  >('provider');
  const [tempSelectedProviders, setTempSelectedProviders] = useState<string[]>(
    []
  );
  const [showBroadcastAcceptModal, setShowBroadcastAcceptModal] =
    useState(false);
  const [selectedAcceptJob, setSelectedAcceptJob] = useState<Job | null>(null);
  const [showBroadcastDeclineModal, setShowBroadcastDeclineModal] =
    useState(false);
  const [selectedDeclineJob, setSelectedDeclineJob] = useState<Job | null>(
    null
  );

  const quoteBroadcastEnabled = useFeatureFlag(FEATURE_FLAGS.quoteBroadcast);

  const {
    data: job,
    isLoading,
    isError,
    refetch: refetchJob,
  } = useFetchJobById(jobId);

  const { data: allJobsData } = useFetchMyJobs({
    limit: 100,
    enabled: Boolean(quoteBroadcastEnabled && job?.batchId),
  });

  const hasScheduledDates = useMemo(() => {
    if (!job?.paymentSchedule || job.paymentSchedule.mode !== 'staged') {
      return false;
    }
    return job.paymentSchedule.stages.some((stage) => stage.estimatedDate);
  }, [job?.paymentSchedule]);

  const batchSiblings: Job[] = useMemo(() => {
    if (!job) return [];
    if (job.batchId) {
      return allJobsData
        ? allJobsData.jobs.filter((j) => j.batchId === job.batchId)
        : [job];
    }
    return [job];
  }, [job, allJobsData]);

  const batchRequest: QuoteRequest | undefined = useMemo(() => {
    if (quoteBroadcastEnabled && job?.batchId && batchSiblings.length > 0) {
      return groupJobsIntoRequests(batchSiblings)[0];
    }
    return undefined;
  }, [quoteBroadcastEnabled, job?.batchId, batchSiblings]);

  const isActiveBatch = Boolean(batchRequest && batchRequest.tab === 'active');
  const quotesInCount = batchRequest?.quotesInCount ?? 0;
  const isBroadcast = Boolean(batchRequest && batchRequest.jobs.length > 1);

  const eligibleProviderCount = useMemo(() => {
    if (!batchRequest) return 0;
    return batchRequest.jobs.filter((j) => {
      const s = getSiblingEffectiveState(j);
      return s !== 'declined' && s !== 'cancelled';
    }).length;
  }, [batchRequest]);

  const allQuotesReceived =
    isActiveBatch &&
    eligibleProviderCount > 0 &&
    quotesInCount >= eligibleProviderCount;

  const canCompareQuotes = isActiveBatch && quotesInCount >= 1;
  const showS8 = canCompareQuotes && (allQuotesReceived || manualCompareView);
  const showS7 = isActiveBatch && !showS8;

  const liveQuoteCount = useMemo(() => {
    if (!batchRequest) return 0;
    return batchRequest.jobs.filter(
      (j) => getSiblingEffectiveState(j) === 'quoted-live'
    ).length;
  }, [batchRequest]);

  const useHorizontalSummary =
    showS8 && liveQuoteCount > HORIZONTAL_SUMMARY_THRESHOLD;

  const rep = useMemo(() => {
    if (batchRequest) return pickRepresentativeJob(batchRequest);
    return job;
  }, [batchRequest, job]);

  const isAccepted = Boolean(
    rep &&
      (rep.status === 'Accepted' ||
        rep.status === 'Scheduled' ||
        rep.status === 'InProgress' ||
        rep.status === 'Completed')
  );

  const rawServiceTitle =
    rep?.serviceTitle ||
    rep?.serviceName ||
    rep?.service?.title ||
    job?.serviceTitle ||
    job?.serviceName ||
    job?.service?.title ||
    'Job Request';

  const headerTitle = isBroadcast
    ? isAccepted
      ? rawServiceTitle
      : 'Multi-Provider Quote Request'
    : rawServiceTitle;

  const showBroadcastChip = isBroadcast;

  const handleBroadcastCancelClose = () => {
    setShowBroadcastCancelModal(false);
    setBroadcastCancelStep('provider');
    setTempSelectedProviders([]);
    setSelectedCancelTargets([]);
    setSelectedCancelReason('');
  };

  const status: JobStatus | undefined = job?.status;
  const displayStatus = rep ? getJobDisplayStatus(rep) : 'Requested';
  const isProviderInitiated = job?.initiatedBy === 'provider';
  const isPendingAction =
    status === ENGAGEMENT_STATUS.REQUESTED ||
    status === ENGAGEMENT_STATUS.QUOTED;

  const quoteExpiry = getQuoteExpiryInfo(job?.quoteExpiresAt);
  const isQuoted = status === ENGAGEMENT_STATUS.QUOTED;
  const isExpiredStatus = status === ENGAGEMENT_STATUS.EXPIRED;
  const quoteLapsed = isQuoted && Boolean(quoteExpiry?.isExpired);
  const showQuoteExpired = isExpiredStatus || quoteLapsed;

  const canAccept = isQuoted && !quoteLapsed;
  const canDecline = isQuoted && !quoteLapsed;
  const canRequestChanges = isQuoted && !quoteLapsed && !job?.batchId;
  const canCancelChangeRequest =
    status === ENGAGEMENT_STATUS.CHANGESREQUESTED && !job?.batchId;

  const canCancelRequest = isBroadcast
    ? Boolean(
        batchRequest &&
          batchRequest.jobs.some((j) => {
            const s = getSiblingEffectiveState(j);
            return s === 'requested';
          })
      )
    : status === ENGAGEMENT_STATUS.REQUESTED;

  const CANCEL_BOOKING_STATUSES: JobStatus[] = [
    ENGAGEMENT_STATUS.ACCEPTED,
    ENGAGEMENT_STATUS.SCHEDULED,
    ENGAGEMENT_STATUS.INPROGRESS,
  ];
  const canCancelBooking = Boolean(
    status && CANCEL_BOOKING_STATUSES.includes(status)
  );

  const canConfirmCompletion = status === ENGAGEMENT_STATUS.AWAITING;
  const isCompletionConfirmed = status === ENGAGEMENT_STATUS.COMPLETED;

  // ── HANDLERS ──
  const handleRequestChangesClick = () => {
    setHasRequestedChanges(true);
    setRevealSignal((prev) => prev + 1);
  };

  const handleAskQuestionAbout = (targetJob: Job) => {
    if (!targetJob.providerId || !targetJob.businessId) {
      addToast({
        title: t(
          'jobs.contact_provider_error_title',
          'Unable to contact provider'
        ),
        description: t(
          'jobs.contact_provider_error',
          'Provider contact details are not available for this request.'
        ),
        color: 'danger',
      });
      return;
    }
    createConversation.mutate({
      senderId: targetJob.customerId,
      receiverId: targetJob.providerId,
      businessId: targetJob.businessId,
    });
  };

  const handleBroadcastAcceptClose = () => {
    setShowBroadcastAcceptModal(false);
    setSelectedAcceptJob(null);
  };

  const handleBroadcastAcceptConfirm = async (selectedJob: Job) => {
    try {
      await acceptMutation.mutateAsync(selectedJob.jobId);
      handleBroadcastAcceptClose();
    } catch {
      // Handled by mutation toast
    }
  };

  const handleBroadcastDeclineClose = () => {
    setShowBroadcastDeclineModal(false);
    setSelectedDeclineJob(null);
  };

  const handleBroadcastDeclineConfirm = async (selectedJob: Job) => {
    try {
      await declineMutation.mutateAsync({
        jobId: selectedJob.jobId,
        declinedNote: undefined,
      });
      handleBroadcastDeclineClose();
    } catch {}
  };

  // ===== HELPER FUNCTION =====
  const shouldShowBroadcastQuoteSelector = (): boolean => {
    if (!isBroadcast || !batchRequest) return false;
    const quotedJobs = batchRequest.jobs.filter(
      (j) => getSiblingEffectiveState(j) === 'quoted-live'
    );
    return quotedJobs.length > 1;
  };

  const handleConfirmAcceptBroadcast = async () => {
    if (!acceptTarget?.batchId) return;
    try {
      await acceptBroadcastMutation.mutateAsync({
        batchId: acceptTarget.batchId,
        jobId: acceptTarget.jobId,
      });
      const winner = acceptTarget;
      setAcceptTarget(null);
      navigate(
        all_routes.customerJob.replace(
          ':jobId',
          winner.displayId || winner.jobId
        )
      );
    } catch {
      // Handled by mutation toast
    }
  };

  const handleCancelBatch = async (reason?: string) => {
    if (!batchRequest) return;
    const openJobs = batchRequest.jobs.filter((j) => {
      const s = getSiblingEffectiveState(j);
      return s === 'requested';
    });
    for (const openJob of openJobs) {
      await cancelMutation.mutateAsync({
        jobId: openJob.jobId,
        cancelledNote: reason,
      });
    }
    refetchJob();
  };

  const handleConfirmCancellation = async (reasonNoteOverride?: string) => {
    if (!job) return;

    const reasonNote = reasonNoteOverride ?? selectedCancelReason ?? undefined;

    if (isBroadcast) {
      if (selectedCancelTargets.includes('ALL')) {
        await handleCancelBatch(reasonNote);
        return;
      }

      for (const targetJobId of selectedCancelTargets) {
        await cancelMutation.mutateAsync({
          jobId: targetJobId,
          cancelledNote: reasonNote,
        });
      }
      refetchJob();
      return;
    }

    // Non-broadcast
    await cancelMutation.mutateAsync({
      jobId: job.jobId,
      cancelledNote: reasonNote,
    });
    refetchJob();
  };

  const contactProvider = () => {
    if (!job) return;
    if (isBroadcast && batchRequest && batchRequest.jobs.length > 1) {
      setShowContactProviderModal(true);
      return;
    }

    if (!job.providerId || !job.businessId) {
      addToast({
        title: t(
          'jobs.contact_provider_error_title',
          'Unable to contact provider'
        ),
        description: t(
          'jobs.contact_provider_error',
          'Provider contact details are not available for this request.'
        ),
        color: 'danger',
      });
      return;
    }

    createConversation.mutate({
      senderId: job.customerId,
      receiverId: job.providerId,
      businessId: job.businessId,
    });
  };

  const handleAddFullScheduleToCalendar = () => {
    if (!job) return;
    const scheduleUrl = `/api/v1/job/${job.jobId}/schedule.ics`;
    const link = document.createElement('a');
    link.href = scheduleUrl;
    link.download = `schedule-${job.jobId}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleAddStageToCalendar = (stageKey: string, stageName: string) => {
    if (!job) return;
    const scheduleUrl = `/api/v1/job/${job.jobId}/schedule/${stageKey}.ics`;
    const link = document.createElement('a');
    link.href = scheduleUrl;
    link.download = `stage-${stageName}-${job.jobId}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const BackLink = () => (
    <button
      type="button"
      onClick={() => navigate(all_routes.customerJobRequests)}
      className="group flex items-center gap-2 text-xs font-semibold text-slate-500 hover:text-primary transition-colors mb-4 cursor-pointer w-fit"
    >
      <div className="p-1.5 rounded-lg bg-slate-100 group-hover:bg-primary/10 transition-colors">
        <FaArrowLeft size={10} />
      </div>
      {t('jobs.back_to_list', 'Back to My Job Requests')}
    </button>
  );

  // ── EARLY RETURNS (Placed strictly after all hooks) ──
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-28 space-y-3">
        <Spinner size="lg" color="primary" />
        <p className="text-xs font-semibold text-slate-500">
          {t('jobs.loading_detail', 'Fetching job details…')}
        </p>
      </div>
    );
  }

  if (isError || !job) {
    return (
      <div className="max-w-xl mx-auto py-12">
        <BackLink />
        <ModernCard
          title={t('jobs.error_title', 'Unable to Load Request')}
          subtitle={t(
            'jobs.detail_error',
            'We could not load this job request.'
          )}
          className="border-red-200 bg-red-50/20"
        >
          <div className="flex justify-center pt-2">
            <CustomButton
              label={t('jobs.back_to_list', 'Back to My Job Requests')}
              color="primary"
              variant="flat"
              onPress={() => navigate(all_routes.customerJobRequests)}
            />
          </div>
        </ModernCard>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto pb-12">
      <BackLink />

      {/* Hero Banner Page Header */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-[#163544] via-[#1a4154] to-[#0095B7] text-white p-2 sm:p-6 mb-4 shadow-md">
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl sm:text-2xl font-semibold tracking-tight text-white mt-1">
                {headerTitle}
              </h1>
              {showBroadcastChip && (
                <CustomChip
                  label="Broadcast"
                  color="primary"
                  variant="flat"
                  size="sm"
                  className="font-bold text-xs bg-white text-primary border-1 border-primary"
                />
              )}
            </div>

            <p className="text-xs sm:text-xs text-slate-200/80 font-medium">
              ID: {job.referenceCode || job.displayId || job.jobId}
              {job.createdAt && ` · Submitted on ${formatDate(job.createdAt)}`}
            </p>
          </div>

          {/* Status Chips */}
          <div className="flex items-center gap-2.5 flex-wrap shrink-0">
            <CustomChip
              label={getEngagementStatusLabel(displayStatus)}
              color={getEngagementStatusChipColor(displayStatus) as any}
              variant="solid"
              size="sm"
              className="font-semibold text-xs tracking-wide uppercase text-xs"
            />
            {isProviderInitiated && (
              <CustomChip
                label={t('jobs.provider_initiated', 'Provider Direct Quote')}
                color="secondary"
                variant="flat"
                size="sm"
                className="bg-white/10 text-xs text-white border border-white/20"
              />
            )}
          </div>
        </div>
      </div>

      {/* Main Tabs Container */}
      <Tabs
        aria-label="Job detail tabs"
        color="primary"
        variant="underlined"
        defaultSelectedKey="review"
        classNames={{
          tabList: 'gap-6 border-b border-slate-200 w-full mb-6',
          tab: 'pb-3 text-xs font-bold',
          cursor: 'bg-primary h-0.5',
        }}
      >
        {/* ── Tab 1: Quote Review ──────────────────────────────────────── */}
        <Tab key="review" title={t('jobs.tab_review', 'Quote Review')}>
          {showS8 && batchRequest ? (
            <div className="space-y-4">
              {useHorizontalSummary && (
                <RequestSummaryCard
                  job={job}
                  jobs={batchRequest.jobs}
                  t={t}
                  orientation="horizontal"
                />
              )}
              <div
                className={
                  useHorizontalSummary
                    ? ''
                    : 'grid grid-cols-1 lg:grid-cols-4 gap-6 items-start'
                }
              >
                {!useHorizontalSummary && (
                  <div className="lg:col-span-1">
                    <RequestSummaryCard
                      job={job}
                      jobs={batchRequest.jobs}
                      t={t}
                    />
                  </div>
                )}
                <div className={useHorizontalSummary ? '' : 'lg:col-span-3'}>
                  <QuoteComparisonPanel
                    request={batchRequest}
                    onAskQuestion={handleAskQuestionAbout}
                    onAccept={setAcceptTarget}
                    isAccepting={acceptBroadcastMutation.isPending}
                    allQuotesReceived={allQuotesReceived}
                    onBackToTracking={
                      !allQuotesReceived
                        ? () => setManualCompareView(false)
                        : undefined
                    }
                  />
                </div>
              </div>
            </div>
          ) : (
            /* Standard 4-Equal-Part Layout Matrix: 1/4 (Summary), 2/4 (Quote), 1/4 (Actions + Negotiation) */
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 items-start">
              {/* Column 1: Request Summary (1/4 Width) */}
              <div className="lg:col-span-1">
                <RequestSummaryCard job={job} t={t} />
              </div>

              {/* Column 2 & 3: Provider's Quote Details (2/4 Width) */}
              <div className="lg:col-span-2 space-y-4">
                {showS7 && batchRequest ? (
                  <QuoteTrackingPanel
                    request={batchRequest}
                    onViewJob={(j) =>
                      navigate(
                        all_routes.customerJob.replace(
                          ':jobId',
                          j.displayId || j.jobId
                        )
                      )
                    }
                    onCancelAll={handleCancelBatch}
                    isCancelling={cancelMutation.isPending}
                  />
                ) : (
                  /* Single Provider Quote Card */
                  <ModernCard
                    title={
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-emerald-500/10 text-emerald-600 flex items-center justify-center font-bold">
                          <FaTag size={16} />
                        </div>
                        <span className="font-semibold text-sm text-slate-800">
                          {t('jobs.provider_quote', "Provider's Quote")}
                        </span>
                      </div>
                    }
                  >
                    <div className="space-y-5">
                      {/* Recorded Scope Call notes */}
                      {isProviderInitiated &&
                        job.promptAnswers &&
                        job.promptAnswers.length > 0 && (
                          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200/80 space-y-2">
                            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                              {t(
                                'jobs.recorded_from_call',
                                'Notes Recorded From Call'
                              )}
                            </p>
                            <div className="space-y-2">
                              {job.promptAnswers.map((item, idx) => (
                                <div
                                  key={idx}
                                  className="text-xs bg-white p-2.5 rounded-lg border border-slate-100"
                                >
                                  <p className="text-xs text-slate-400">
                                    {item.label || item.promptText}
                                  </p>
                                  <p className="text-slate-700 text-xs mt-0.5">
                                    {item.answer}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                      {/* Duration & Dates Overview Grid */}
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 p-3.5 rounded-xl bg-slate-50 border border-slate-200/60">
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                            {t('jobs.estimated_duration', 'Duration')}
                          </p>
                          <p className="text-xs font-semibold text-slate-800 mt-0.5">
                            {typeof job.estimatedDurationDays === 'number'
                              ? `${job.estimatedDurationDays} day${
                                  job.estimatedDurationDays === 1 ? '' : 's'
                                }`
                              : '—'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                            {t('jobs.proposed_start', 'Start Date')}
                          </p>
                          <p className="text-xs font-semibold text-slate-800 mt-0.5">
                            {formatDate(job.proposedStartDate)}
                          </p>
                        </div>
                        {job.proposedEndDate && (
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                              {t('jobs.proposed_end', 'End Date')}
                            </p>
                            <p className="text-xs font-semibold text-slate-800 mt-0.5">
                              {formatDate(job.proposedEndDate)}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Assigned Staff — optional, only render when present */}
                      {job.staff && (
                        <div className="p-3 rounded-xl bg-slate-50 border border-slate-200/60">
                          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1.5">
                            <FiUserCheck className="text-primary" size={14} />
                            {t('jobs.assigned_staff', 'Assigned Staff')}
                          </p>
                          <div className="flex items-center gap-3">
                            <div className="h-7 w-7 shrink-0 rounded-full bg-primary text-white text-xs font-bold flex items-center justify-center">
                              {(job.staff.name || '?')
                                .split(' ')
                                .filter(Boolean)
                                .slice(0, 2)
                                .map((part) => part[0]?.toUpperCase())
                                .join('') || '?'}
                            </div>
                            <div className="min-w-0">
                              <p className="text-xs font-semibold text-slate-800 truncate">
                                {job.staff.name}
                              </p>
                              {job.staff.email && (
                                <p className="text-[11px] text-slate-500 truncate">
                                  {job.staff.email}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Line Items Breakdown */}
                      {job.lineItems && job.lineItems.length > 0 && (
                        <LineItemsBreakdown
                          lineItems={job.lineItems}
                          adjustment={job.adjustment}
                          addonSubtotal={job.addonSubtotal || 0}
                          additionalServices={
                            Array.isArray(job.additionalServices)
                              ? job.additionalServices
                              : []
                          }
                          quotedPrice={job.total || job.totalPrice || 0}
                        />
                      )}

                      {/* Inclusions / Exclusions / Guarantee — optional, only render when present */}
                      {((job.inclusions?.length ?? 0) > 0 ||
                        (job.exclusions?.length ?? 0) > 0 ||
                        Boolean(job.guarantee)) && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {/* Inclusions */}
                          {(job.inclusions?.length ?? 0) > 0 && (
                            <div className="p-3 rounded-xl bg-emerald-50/60 border border-emerald-200/70">
                              <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700 mb-1.5 flex items-center gap-1.5">
                                <MdCheckCircle size={14} />
                                {t('jobs.inclusions', "What's Included")}
                              </p>
                              <ul className="space-y-1">
                                {job.inclusions!.map((item, idx) => (
                                  <li
                                    key={idx}
                                    className="text-xs text-slate-700 flex items-start gap-1.5"
                                  >
                                    <span className="text-emerald-600 mt-0.5">
                                      •
                                    </span>
                                    <span>{item}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Exclusions */}
                          {(job.exclusions?.length ?? 0) > 0 && (
                            <div className="p-3 rounded-xl bg-rose-50/60 border border-rose-200/70">
                              <p className="text-xs font-semibold uppercase tracking-wider text-rose-700 mb-1.5 flex items-center gap-1.5">
                                <MdOutlineBlock size={14} />
                                {t('jobs.exclusions', "What's Excluded")}
                              </p>
                              <ul className="space-y-1">
                                {job.exclusions!.map((item, idx) => (
                                  <li
                                    key={idx}
                                    className="text-xs text-slate-700 flex items-start gap-1.5"
                                  >
                                    <span className="text-rose-500 mt-0.5">
                                      •
                                    </span>
                                    <span>{item}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Guarantee — full width when present */}
                          {job.guarantee && (
                            <div className="p-3 rounded-xl bg-sky-50/60 border border-sky-200/70 sm:col-span-2">
                              <p className="text-xs font-semibold uppercase tracking-wider text-sky-700 mb-1.5 flex items-center gap-1.5">
                                <FaShieldHalved size={14} />
                                {t('jobs.guarantee', 'Guarantee')}
                              </p>
                              <p className="text-xs text-slate-700 leading-relaxed">
                                {job.guarantee}
                              </p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Staged Payment Schedule Breakdown */}
                      {job.paymentSchedule &&
                        job.paymentSchedule.mode === 'staged' && (
                          <PaymentScheduleBreakdown
                            schedule={job.paymentSchedule}
                            onAddToCalendar={handleAddStageToCalendar}
                          />
                        )}

                      {/* Provider Notes */}
                      {job.providerNotes && (
                        <div className="p-3 rounded-xl bg-slate-50 border border-slate-200/60">
                          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">
                            {t('jobs.provider_notes', 'Notes from Provider')}
                          </p>
                          <p className="text-xs text-slate-700 whitespace-pre-line leading-relaxed">
                            {job.providerNotes}
                          </p>
                        </div>
                      )}
                    </div>
                  </ModernCard>
                )}
              </div>

              {/* Column 4: Dedicated Request Actions & Alerts Card + Quote Negotiation Thread (1/4 Width) */}
              <div className="lg:col-span-1 space-y-4">
                <ModernCard
                  title={
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center font-bold">
                        <MdOutlineBolt size={20} />
                      </div>
                      <span className="font-semibold text-sm text-slate-800">
                        {t('jobs.request_actions', 'Actions')}
                      </span>
                    </div>
                  }
                >
                  <div className="space-y-4">
                    {/* Pending Response Notice */}
                    {isPendingAction && !canAccept && !canDecline && (
                      <div className="flex items-center gap-3 p-2 rounded-xl bg-amber-50 border border-amber-200 text-amber-800">
                        <FiClock
                          className="shrink-0 text-amber-600"
                          size={18}
                        />
                        <p className="text-xs font-medium">
                          {t(
                            'jobs.pending_hint',
                            'Awaiting formal quote response from provider.'
                          )}
                        </p>
                      </div>
                    )}

                    {/* Changes Requested Notice */}
                    {status === ENGAGEMENT_STATUS.CHANGESREQUESTED && (
                      <div className="flex items-center gap-3 p-2 rounded-xl bg-amber-50 border border-amber-200 text-amber-800">
                        <FiClock
                          className="shrink-0 text-amber-600"
                          size={18}
                        />
                        <p className="text-xs font-medium">
                          {t(
                            'jobs.changes_requested_hint',
                            'Waiting for the provider to respond to your requested changes.'
                          )}
                        </p>
                      </div>
                    )}

                    {/* Expiry Countdown Alert */}
                    {isQuoted && quoteExpiry && !quoteExpiry.isExpired && (
                      <CustomAlert
                        title={
                          quoteExpiry.isUrgent
                            ? t('jobs.quote_expires_soon', 'Quote Expires Soon')
                            : t(
                                'jobs.quote_expires_in_days',
                                `Valid for ${quoteExpiry.days} more day${
                                  quoteExpiry.days === 1 ? '' : 's'
                                }`
                              )
                        }
                        description={
                          quoteExpiry.isUrgent
                            ? t(
                                'jobs.quote_expires_soon_desc',
                                `Expires in ${quoteExpiry.hours} hours`
                              )
                            : t(
                                'jobs.quote_expires_in_desc',
                                `Lock in price before ${formatDate(
                                  job.quoteExpiresAt ?? undefined
                                )}`
                              )
                        }
                        color={quoteExpiry.isUrgent ? 'warning' : 'primary'}
                        variant="faded"
                      />
                    )}

                    {/* Quote Expired Alert */}
                    {showQuoteExpired && (
                      <CustomAlert
                        title={t('jobs.quote_expired', 'Quote Expired')}
                        description={t(
                          'jobs.quote_expired_desc',
                          'This quote is no longer valid. Contact provider to request an updated quote.'
                        )}
                        color="warning"
                        variant="faded"
                      />
                    )}

                    {/* Completion Confirmed Badge */}
                    {isCompletionConfirmed && (
                      <div className="flex items-center justify-center gap-1.5 p-2.5 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-bold">
                        <MdCheckCircle size={16} />
                        <span>Completion confirmed</span>
                      </div>
                    )}

                    {/* Compare Quotes Early Button */}
                    {showS7 && canCompareQuotes && (
                      <div className="p-3 rounded-xl bg-primary/5 border border-primary/20 space-y-2">
                        <p className="text-xs font-semibold text-primary">
                          {t(
                            'jobs.compare_quotes_hint',
                            `${quotesInCount} quotes received so far.`
                          )}
                        </p>
                        <CustomButton
                          label={t(
                            'jobs.compare_quotes_now',
                            'Compare Quotes Received'
                          )}
                          color="primary"
                          variant="solid"
                          className="text-xs"
                          onPress={() => setManualCompareView(true)}
                          startContent={<FaScaleBalanced size={14} />}
                          fullWidth
                        />
                      </div>
                    )}

                    {/* Actions Stack */}
                    <div className="space-y-2.5 pt-2 border-t border-slate-100">
                      {/* Primary Contact Provider */}
                      <CustomButton
                        label={t('jobs.contact_provider', 'Contact Provider')}
                        color="primary"
                        variant="solid"
                        className="text-xs"
                        isLoading={createConversation.isPending}
                        isDisabled={createConversation.isPending}
                        onPress={contactProvider}
                        startContent={<FiMessageCircle size={16} />}
                        fullWidth
                      />

                      {/* Accept Quote */}
                      {canAccept && (
                        <>
                          {shouldShowBroadcastQuoteSelector() &&
                          batchRequest ? (
                            <>
                              <CustomButton
                                label={t('jobs.accept_quote', 'Accept Quote')}
                                color="primary"
                                variant="solid"
                                className="w-full bg-primary/20 text-primary text-xs hover:bg-primary/30"
                                onPress={() =>
                                  setShowBroadcastAcceptModal(true)
                                }
                              />

                              <BroadcastAcceptDeclineModal
                                isOpen={showBroadcastAcceptModal}
                                onClose={handleBroadcastAcceptClose}
                                quotedJobs={batchRequest.jobs}
                                action="accept"
                                selectedJob={selectedAcceptJob}
                                onSelectedJobChange={setSelectedAcceptJob}
                                onConfirm={handleBroadcastAcceptConfirm}
                                isLoading={acceptMutation.isPending}
                              />
                            </>
                          ) : (
                            <CommonConfirmModal
                              buttonLabel={t(
                                'jobs.accept_quote',
                                'Accept Quote'
                              )}
                              buttonColor="primary"
                              buttonVariant="flat"
                              triggerClassName="w-full text-xs bg-primary/20 text-primary hover:bg-primary/30"
                              title={t(
                                'jobs.confirm_accept_title',
                                'Accept this quote?'
                              )}
                              description={t(
                                'jobs.confirm_accept_desc',
                                'Accepting locks in the quoted price. The provider will schedule the work.'
                              )}
                              confirmBtnText={t(
                                'jobs.accept_quote',
                                'Accept Quote'
                              )}
                              confirmBtnColor="primary"
                              cancelBtnText={t('jobs.cancel', 'Cancel')}
                              isLoading={acceptMutation.isPending}
                              onConfirm={() => acceptMutation.mutate(job.jobId)}
                            />
                          )}
                        </>
                      )}

                      {/* Add Schedule to Calendar */}
                      {hasScheduledDates && (
                        <CustomButton
                          label={t(
                            'jobs.add_schedule_to_calendar',
                            'Add to Calendar'
                          )}
                          color="secondary"
                          variant="flat"
                          onPress={handleAddFullScheduleToCalendar}
                          startContent={<FiCalendar size={16} />}
                          fullWidth
                        />
                      )}

                      {/* Add Review */}
                      {isCompletionConfirmed && !job.hasCusReviewed && (
                        <CustomButton
                          label={t('jobs.add_review', 'Add Review')}
                          color="secondary"
                          variant="solid"
                          onPress={() => setShowReviewForm(true)}
                          fullWidth
                        />
                      )}

                      {/* Confirm Completion */}
                      {canConfirmCompletion && (
                        <CommonConfirmModal
                          buttonLabel={t(
                            'jobs.confirm_completion',
                            'Confirm Completion'
                          )}
                          buttonColor="success"
                          buttonVariant="solid"
                          title={t(
                            'jobs.confirm_completion_title',
                            'Confirm work complete?'
                          )}
                          description={t(
                            'jobs.confirm_completion_desc',
                            'Confirm that the work was completed to your satisfaction.'
                          )}
                          confirmBtnText={t(
                            'jobs.confirm_completion',
                            'Confirm Completion'
                          )}
                          confirmBtnColor="success"
                          cancelBtnText={t('jobs.cancel', 'Cancel')}
                          iconType="info"
                          isLoading={confirmCompletionMutation.isPending}
                          onConfirm={() =>
                            confirmCompletionMutation.mutate(job.jobId)
                          }
                          triggerClassName="w-full"
                        />
                      )}

                      {/* Decline Quote */}
                      {canDecline && (
                        <>
                          {shouldShowBroadcastQuoteSelector() &&
                          batchRequest ? (
                            <>
                              <CustomButton
                                label={t('jobs.decline_quote', 'Decline Quote')}
                                color="danger"
                                variant="flat"
                                className="w-full text-xs"
                                onPress={() =>
                                  setShowBroadcastDeclineModal(true)
                                }
                              />

                              <BroadcastAcceptDeclineModal
                                isOpen={showBroadcastDeclineModal}
                                onClose={handleBroadcastDeclineClose}
                                quotedJobs={batchRequest.jobs}
                                action="decline"
                                selectedJob={selectedDeclineJob}
                                onSelectedJobChange={setSelectedDeclineJob}
                                onConfirm={handleBroadcastDeclineConfirm}
                                isLoading={declineMutation.isPending}
                              />
                            </>
                          ) : (
                            <CommonConfirmModal
                              buttonLabel={t(
                                'jobs.decline_quote',
                                'Decline Quote'
                              )}
                              buttonColor="danger"
                              buttonVariant="flat"
                              title={t(
                                'jobs.confirm_decline_title',
                                'Decline this quote?'
                              )}
                              description={t(
                                'jobs.confirm_decline_desc',
                                'Declining closes this request. You can request a new quote if you change your mind.'
                              )}
                              confirmBtnText={t(
                                'jobs.decline_quote',
                                'Decline Quote'
                              )}
                              confirmBtnColor="danger"
                              cancelBtnText={t('jobs.cancel', 'Cancel')}
                              requireReason
                              reasonLabel={t(
                                'jobs.decline_reason_label',
                                'Reason for declining'
                              )}
                              reasonDescription={t(
                                'jobs.decline_reason_desc',
                                "Let the provider know why, if you'd like."
                              )}
                              isLoading={declineMutation.isPending}
                              onConfirm={(reason) =>
                                declineMutation.mutate({
                                  jobId: job.jobId,
                                  declinedNote: reason,
                                })
                              }
                              triggerClassName="w-full"
                            />
                          )}
                        </>
                      )}

                      {/* Request Changes */}
                      {canRequestChanges && (
                        <CommonConfirmModal
                          buttonLabel={t(
                            'jobs.request_changes',
                            'Request Changes'
                          )}
                          buttonColor="warning"
                          buttonVariant="flat"
                          title={t(
                            'jobs.confirm_request_changes_title',
                            'Request changes to this quote?'
                          )}
                          description={t(
                            'jobs.confirm_request_changes_desc',
                            "Tell the provider what you'd like changed. They'll be notified and can send a revised quote."
                          )}
                          confirmBtnText={t(
                            'jobs.request_changes',
                            'Request Changes'
                          )}
                          confirmBtnColor="warning"
                          cancelBtnText={t('jobs.cancel', 'Cancel')}
                          requireReason
                          reasonRequired
                          reasonMinLength={1}
                          reasonMaxLength={2000}
                          reasonLabel={t(
                            'jobs.request_changes_message_label',
                            'What would you like changed?'
                          )}
                          reasonDescription={t(
                            'jobs.request_changes_message_desc',
                            'Describe the changes you need — the provider will see this in the negotiation thread below.'
                          )}
                          isLoading={requestChangesMutation.isPending}
                          onConfirm={(message) => {
                            if (!message) return;
                            handleRequestChangesClick();
                            requestChangesMutation.mutate({
                              jobId: job.jobId,
                              message,
                            });
                          }}
                          triggerClassName="w-full"
                        />
                      )}

                      {/* Cancel Change Request */}
                      {canCancelChangeRequest && (
                        <CommonConfirmModal
                          buttonLabel={t(
                            'jobs.cancel_request_changes',
                            'Cancel Change Request'
                          )}
                          buttonColor="default"
                          buttonVariant="flat"
                          title={t(
                            'jobs.confirm_cancel_request_changes_title',
                            'Cancel this change request?'
                          )}
                          description={t(
                            'jobs.confirm_cancel_request_changes_desc',
                            'This withdraws your request and restores the original quote so you can accept, decline, or request changes again.'
                          )}
                          confirmBtnText={t(
                            'jobs.cancel_request_changes',
                            'Cancel Change Request'
                          )}
                          confirmBtnColor="primary"
                          cancelBtnText={t('jobs.keep_request', 'Keep Waiting')}
                          iconType="info"
                          isLoading={cancelRequestChangesMutation.isPending}
                          onConfirm={() =>
                            cancelRequestChangesMutation.mutate(job.jobId)
                          }
                          triggerClassName="w-full"
                        />
                      )}

                      {/* Cancel Initial Request */}
                      {canCancelRequest && (
                        <>
                          {/* Broadcast Provider Selection Modal */}
                          {isBroadcast ? (
                            <>
                              <CustomButton
                                label={t(
                                  'jobs.cancel_request',
                                  'Cancel Request'
                                )}
                                color="danger"
                                variant="bordered"
                                className="w-full"
                                onPress={() =>
                                  setShowBroadcastCancelModal(true)
                                }
                              />

                              <BroadcastProviderSelectModal
                                isOpen={showBroadcastCancelModal}
                                onClose={handleBroadcastCancelClose}
                                jobs={batchRequest?.jobs ?? []}
                                selectedProviders={tempSelectedProviders}
                                onSelectedProvidersChange={
                                  setTempSelectedProviders
                                }
                                step={broadcastCancelStep}
                                onStepChange={(nextStep) => {
                                  // Commit the provider selection into the "targets" state when advancing
                                  if (nextStep === 'reason') {
                                    setSelectedCancelTargets(
                                      tempSelectedProviders
                                    );
                                  }
                                  setBroadcastCancelStep(nextStep);
                                }}
                                cancelReasons={CUSTOMER_CANCEL_REASONS}
                                selectedReason={selectedCancelReason}
                                onSelectedReasonChange={setSelectedCancelReason}
                                onConfirmCancel={async () => {
                                  await handleConfirmCancellation();
                                  handleBroadcastCancelClose();
                                }}
                                isCancelling={cancelMutation.isPending}
                              />
                            </>
                          ) : (
                            /* Original CommonConfirmModal for non-broadcast requests */
                            <CommonConfirmModal
                              buttonLabel={t(
                                'jobs.cancel_request',
                                'Cancel Request'
                              )}
                              buttonColor="danger"
                              buttonVariant="light"
                              title={t(
                                'jobs.confirm_cancel_title',
                                'Cancel this request?'
                              )}
                              description={t(
                                'jobs.confirm_cancel_desc',
                                'Cancelling closes this request. This action cannot be undone.'
                              )}
                              confirmBtnText={t(
                                'jobs.cancel_request',
                                'Cancel Request'
                              )}
                              confirmBtnColor="danger"
                              cancelBtnText={t(
                                'jobs.keep_request',
                                'Keep Request'
                              )}
                              iconType="danger"
                              requireReason
                              reasonRequired
                              reasonLabel={t(
                                'jobs.cancel_reason_label',
                                'Cancellation Reason'
                              )}
                              reasonDescription={t(
                                'jobs.cancel_reason_desc',
                                'Please select a reason for cancelling.'
                              )}
                              reasonOptions={CUSTOMER_CANCEL_REASONS}
                              otherOptionValue="other"
                              isLoading={cancelMutation.isPending}
                              onConfirm={async (reasonNote) => {
                                await handleConfirmCancellation(reasonNote);
                              }}
                              triggerClassName="w-full"
                            />
                          )}
                        </>
                      )}

                      {/* Cancel Booking */}
                      {canCancelBooking && (
                        <CommonConfirmModal
                          buttonLabel={t(
                            'jobs.cancel_booking',
                            'Cancel Booking'
                          )}
                          buttonColor="danger"
                          buttonVariant="light"
                          title={t(
                            'jobs.confirm_cancel_booking_title',
                            'Cancel this booking?'
                          )}
                          description={t(
                            'jobs.confirm_cancel_booking_desc',
                            'Cancelling closes this booking. This action cannot be undone.'
                          )}
                          confirmBtnText={t(
                            'jobs.cancel_booking',
                            'Cancel Booking'
                          )}
                          confirmBtnColor="danger"
                          cancelBtnText={t('jobs.keep_request', 'Keep Request')}
                          iconType="danger"
                          requireReason
                          reasonRequired
                          reasonLabel={t(
                            'jobs.cancel_reason_label',
                            'Cancellation Reason'
                          )}
                          reasonDescription={t(
                            'jobs.cancel_reason_desc',
                            'Please select a reason for cancelling.'
                          )}
                          reasonOptions={CUSTOMER_CANCEL_REASONS}
                          otherOptionValue="other"
                          isLoading={cancelMutation.isPending}
                          onConfirm={async (reason) => {
                            await cancelMutation.mutateAsync({
                              jobId: job.jobId,
                              cancelledNote: reason,
                            });
                            refetchJob();
                          }}
                          triggerClassName="w-full"
                        />
                      )}
                    </div>
                  </div>
                </ModernCard>

                {/* Quote Negotiation Thread */}
                <QuoteNegotiationThread
                  jobId={job.jobId}
                  currentRevisionNo={job.currentRevisionNo}
                  hasRequestedChanges={hasRequestedChanges}
                  revealSignal={revealSignal}
                />
              </div>
            </div>
          )}
        </Tab>

        {/* ── Tab 2: Timeline & Tracking ────────────────────────────────── */}
        <Tab
          key="timeline"
          title={t('jobs.tab_timeline', 'Timeline & Tracking')}
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Timeline Column */}
            <div className="lg:col-span-2 space-y-4">
              <ModernCard
                title={
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center font-bold">
                      <BiTimeFive size={18} />
                    </div>
                    <span className="font-semibold text-sm text-slate-800">
                      Job Progression Overview
                    </span>
                  </div>
                }
              >
                <JobProgressTimeline displayStatus={displayStatus} job={job} />
              </ModernCard>

              {job.scheduledStartDate && (
                <ModernCard className="border-l-4 border-primary bg-gradient-to-r from-primary/5 via-slate-50 to-transparent">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-xl bg-primary/10 text-primary">
                      <BiCalendarCheck size={24} />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-400">
                        {t('jobs.scheduled_start', 'Scheduled Start Date')}
                      </p>
                      <p className="text-base font-bold text-slate-800 mt-0.5">
                        {formatDate(job.scheduledStartDate)}
                      </p>
                    </div>
                    <div className="hidden sm:block">
                      <CustomChip
                        label="Upcoming"
                        color="primary"
                        variant="flat"
                        size="sm"
                        className="font-bold"
                      />
                    </div>
                  </div>
                </ModernCard>
              )}
            </div>

            {/* Provider Contact Information Card */}
            <div>
              <ModernCard
                title={
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-secondary/10 text-secondary flex items-center justify-center font-bold">
                      <FiUser size={18} />
                    </div>
                    <span className="font-semibold text-sm text-slate-800">
                      {t('jobs.provider_contact', 'Provider Contact')}
                    </span>
                  </div>
                }
              >
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 rounded-xl border border-slate-200/70 bg-slate-50">
                    <div className="p-2 rounded-lg bg-white border border-slate-200 text-slate-600">
                      <FaUserTie size={16} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-400">
                        {t('jobs.name', 'Name')}
                      </p>
                      <p className="text-xs font-semibold text-slate-800 truncate">
                        {job.providerName || '—'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-3 rounded-xl border border-slate-200/70 bg-slate-50">
                    <div className="p-2 rounded-lg bg-white border border-slate-200 text-slate-600">
                      <FaPhone size={16} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-400">
                        {t('jobs.phone', 'Phone')}
                      </p>
                      <p className="text-xs font-semibold text-slate-800 truncate">
                        {job.providerMobile || '—'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-3 rounded-xl border border-slate-200/70 bg-slate-50">
                    <div className="p-2 rounded-lg bg-white border border-slate-200 text-slate-600">
                      <FaRegEnvelope size={16} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-400">
                        {t('jobs.email', 'Email')}
                      </p>
                      <p className="text-xs font-semibold text-slate-800 truncate">
                        {job.providerEmail || '—'}
                      </p>
                    </div>
                  </div>

                  <div className="pt-2">
                    <CustomButton
                      label={t('jobs.contact_provider', 'Contact Provider')}
                      color="primary"
                      variant="solid"
                      size="sm"
                      className="text-xs"
                      isLoading={createConversation.isPending}
                      isDisabled={createConversation.isPending}
                      onPress={contactProvider}
                      startContent={<FiMessageCircle size={16} />}
                      fullWidth
                    />
                  </div>
                </div>
              </ModernCard>
            </div>
          </div>
        </Tab>
      </Tabs>

      {/* Review Modal */}
      <ReviewForm
        isOpen={showReviewForm}
        onClose={() => setShowReviewForm(false)}
        onSuccess={() => {
          refetchJob();
        }}
        providerId={job?.providerId}
        serviceId={job?.serviceId}
        jobId={job?.jobId}
        businessId={job?.businessId}
        serviceName={job?.serviceTitle || job?.serviceName}
      />

      {/* Accept Broadcast Confirmation Modal */}
      <Modal
        isOpen={Boolean(acceptTarget)}
        onOpenChange={(open) => {
          if (!open) setAcceptTarget(null);
        }}
      >
        <ModalContent className="rounded-2xl">
          <ModalHeader className="font-bold text-slate-800">
            {acceptTarget
              ? `Accept Quote from ${providerLabel(acceptTarget)}?`
              : 'Accept this quote?'}
          </ModalHeader>
          <ModalBody>
            <p className="text-xs text-slate-600 leading-relaxed">
              {acceptTarget?.quotedPrice != null
                ? `${providerLabel(acceptTarget)} will be booked for ${formatCurrency(
                    acceptTarget.quotedPrice
                  )}.`
                : `${
                    acceptTarget ? providerLabel(acceptTarget) : 'This provider'
                  } will be booked.`}{' '}
              Other local providers in this request batch will be automatically
              notified that another quote was selected.
            </p>
          </ModalBody>
          <ModalFooter>
            <CustomButton
              label="Keep Comparing"
              color="default"
              variant="flat"
              onPress={() => setAcceptTarget(null)}
              isDisabled={acceptBroadcastMutation.isPending}
            />
            <CustomButton
              label={
                acceptTarget
                  ? `Accept ${providerLabel(acceptTarget)}`
                  : 'Accept'
              }
              color="primary"
              variant="solid"
              onPress={handleConfirmAcceptBroadcast}
              isLoading={acceptBroadcastMutation.isPending}
            />
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Provider Contact Selection Modal for Broadcast Requests */}
      <Modal
        isOpen={showContactProviderModal}
        onOpenChange={(open) => {
          if (!open) setShowContactProviderModal(false);
        }}
        hideCloseButton
        size="2xl"
        scrollBehavior="inside"
      >
        <ModalContent className="rounded-2xl max-h-[80vh] flex flex-col">
          <ModalHeader className="flex flex-col items-start gap-1 font-bold text-lg text-slate-800 w-full">
            <span className="block w-full font-bold text-lg text-slate-800">
              Contact a Provider
            </span>
            <p className="block w-full text-xs font-normal text-slate-500 leading-snug">
              Select which provider from this broadcast batch you would like to
              start a conversation with:
            </p>
          </ModalHeader>
          <ModalBody>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {batchRequest?.jobs.map((itemJob) => {
                const pName = providerLabel(itemJob);
                const sState = getSiblingEffectiveState(itemJob);
                return (
                  <div
                    key={itemJob.jobId}
                    className="flex items-center justify-between p-3 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors"
                  >
                    <div>
                      <p className="text-xs font-semibold text-slate-800">
                        {pName}
                      </p>
                      <p className="text-xs text-slate-400 capitalize">
                        {sState === 'quoted-live' ? 'Quoted' : sState}
                      </p>
                    </div>
                    <CustomButton
                      label="Contact Provider"
                      color="primary"
                      variant="solid"
                      size="sm"
                      className="text-xs"
                      startContent={<FiMessageCircle size={14} />}
                      isLoading={createConversation.isPending}
                      onPress={() => {
                        setShowContactProviderModal(false);
                        handleAskQuestionAbout(itemJob);
                      }}
                    />
                  </div>
                );
              })}
            </div>
          </ModalBody>
          <ModalFooter>
            <CustomButton
              label="Cancel"
              color="default"
              variant="flat"
              size="sm"
              className="text-xs"
              onPress={() => setShowContactProviderModal(false)}
            />
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default JobDetail;
